import type { IEmployee } from '@kfolks/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/api.client'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { StaticComboBoxField } from '@/modules/forms/StaticComboBoxField'
import { useContractTypes, useGeoLocations, useDepartments, useLeadershipTypes } from '@/hooks/useMaster'

interface EmployeeFilters {
	displayName?: string
	firstName?: string
	lastName?: string
	contractTypeId?: string
	geoLocationId?: string
	departmentId?: string
	leadershipTypeId?: string
	personTypeId?: string
	contractorId?: string
	managerId?: string
	isActive?: string
}

interface EmployeeQueryParams {
	page: number
	limit: number
	sort: string
	order: 'asc' | 'desc'
	filters?: EmployeeFilters
}

interface EntraSearchResult {
	entraDirectoryId: string
	displayName: string
	firstName?: string
	lastName?: string
	email: string | null
	entityType: string
}

interface MasterPersonType {
	personTypeId: string
	code: string
	label: string
	isActive: boolean
}

interface ContractorRecord {
	contractorId: string
	name: string
	code: string
	isActive: boolean
}

interface CreateEmployeeInput {
	displayName: string
	firstName?: string
	lastName?: string
	entraDirectoryId?: string | null
	contractTypeId?: string | null
	geoLocationId?: string | null
	departmentId?: string | null
	leadershipTypeId?: string | null
	personTypeId?: string | null
	contractorId?: string | null
	managerId?: string | null
	isActive?: boolean
	comment?: string
}

export function EmployeesPage() {
	const queryClient = useQueryClient()
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [editingEmployee, setEditingEmployee] = useState<IEmployee | null>(null)
	const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
	const [deleteComment, setDeleteComment] = useState('')

	const [queryParams, setQueryParams] = useState<EmployeeQueryParams>({
		page: 1,
		limit: 200,
		sort: 'createdAt',
		order: 'desc',
		filters: {},
	})

	const [tempFilters, setTempFilters] = useState<EmployeeFilters>({})

	// Form state for create/edit
	const [formData, setFormData] = useState<CreateEmployeeInput>({ displayName: '' })
	const [entraSearch, setEntraSearch] = useState('')
	const [entraResults, setEntraResults] = useState<EntraSearchResult[]>([])

	// Fetch employees
	const { data: employees, isLoading } = useQuery({
		queryKey: ['employees', queryParams],
		queryFn: async () => {
			const params = new URLSearchParams()
			params.append('page', queryParams.page.toString())
			params.append('limit', queryParams.limit.toString())
			params.append('sort', queryParams.sort)
			params.append('order', queryParams.order)
			if (queryParams.filters) {
				Object.entries(queryParams.filters).forEach(([key, value]: [string, string | undefined]) => {
					if (value && value.trim() !== '') params.append(`filters[${key}]`, value)
				})
			}
			const response = await apiClient.get<IEmployee[]>(`/employees?${params.toString()}`)
			return response.data || []
		},
	})

	// Separate unfiltered fetch for the manager picker / filter / table column.
	// The table-driven `employees` query is paginated and filtered, so it would
	// hide candidates that don't match the current filter — wrong for a picker
	// that has to offer "anyone in the org" and for the table column that has
	// to resolve a managerId even when its row is filtered out of the view.
	const { data: allEmployees } = useQuery({
		queryKey: ['employees', 'all-for-manager-picker'],
		queryFn: async () => {
			const params = new URLSearchParams()
			params.append('page', '1')
			params.append('limit', '1000')
			params.append('sort', 'displayName')
			params.append('order', 'asc')
			const response = await apiClient.get<IEmployee[]>(`/employees?${params.toString()}`)
			return response.data || []
		},
		staleTime: 60_000,
	})

	// Fetch master data
	const { data: contractTypes } = useContractTypes()
	const { data: geoLocations } = useGeoLocations()
	const { data: departments } = useDepartments()
	const { data: leadershipTypes } = useLeadershipTypes()
	const { data: personTypes } = useQuery({
		queryKey: ['person-types'],
		queryFn: async () => {
			const response = await apiClient.get<MasterPersonType[]>('/employees/master/person-types')
			return response.data || []
		},
	})
	const { data: contractors } = useQuery({
		queryKey: ['contractors'],
		queryFn: async () => {
			const response = await apiClient.get<ContractorRecord[]>('/employees/contractors')
			return response.data || []
		},
	})

	// Build ComboBox option lists from the master-data hook payloads. Memoised
	// so the StaticComboBoxField doesn't see a new array identity per render.
	const contractTypeOptions = useMemo(
		() => (contractTypes ?? []).map((ct) => ({ label: ct.label, value: ct.contractTypeId })),
		[contractTypes],
	)
	const geoLocationOptions = useMemo(
		() => (geoLocations ?? []).map((gl) => ({ label: gl.label, value: gl.geoLocationId })),
		[geoLocations],
	)
	const leadershipTypeOptions = useMemo(
		() => (leadershipTypes ?? []).map((lt) => ({ label: lt.label, value: lt.leadershipTypeId })),
		[leadershipTypes],
	)
	const personTypeOptions = useMemo(
		() => (personTypes ?? []).map((pt) => ({ label: pt.label, value: pt.personTypeId })),
		[personTypes],
	)
	const contractorOptions = useMemo(
		() => (contractors ?? []).map((c) => ({ label: c.name, value: c.contractorId })),
		[contractors],
	)
	// The /employees/master/departments endpoint returns a nested tree (root
	// nodes with `children`), not a flat list. Flatten it once and reuse for the
	// hierarchical picker, the filter dropdown, and the table column — otherwise
	// only root rows are visible (everything nested under "Keenfolks" disappears).
	const flatDepartments = useMemo(() => flattenDepartmentTree(departments ?? []), [departments])
	// Manager picker options. Exclude the row being edited so an admin can't
	// accidentally set the employee as their own manager (would violate the
	// self-FK invariant the workflow walker assumes).
	const managerPickerOptions = useMemo(() => {
		const editingId = editingEmployee?.employeeId
		return (allEmployees ?? [])
			.filter((e) => e.employeeId !== editingId)
			.map((e) => ({ label: e.displayName, value: e.employeeId }))
	}, [allEmployees, editingEmployee])
	// Filter dropdown options — no self-exclusion (the table can be filtered by
	// "all employees who report to X" regardless of whether X is on screen).
	const managerFilterOptions = useMemo(
		() => (allEmployees ?? []).map((e) => ({ label: e.displayName, value: e.employeeId })),
		[allEmployees],
	)
	// Departments render hierarchically — "Keenfolks › Tech › Tech Frontend" —
	// so a leaf is distinguishable from its siblings. Same convention as the
	// request-center departments data source.
	const departmentOptions = useMemo(() => buildDepartmentOptions(flatDepartments), [flatDepartments])

	// Mutations
	const createMutation = useMutation({
		mutationFn: async (data: CreateEmployeeInput) => {
			const response = await apiClient.post<IEmployee>('/employees', data)
			return response.data
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['employees'] })
			setShowCreateModal(false)
			resetForm()
		},
	})

	const updateMutation = useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<CreateEmployeeInput & { isActive?: boolean }> }) => {
			const response = await apiClient.put<IEmployee>(`/employees/${id}`, data)
			return response.data
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['employees'] })
			setEditingEmployee(null)
			resetForm()
		},
	})

	const deleteMutation = useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/employees/${id}`)
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['employees'] })
			setDeleteConfirm(null)
		},
	})

	const resetForm = () => {
		setFormData({ displayName: '' })
		setEntraSearch('')
		setEntraResults([])
	}

	const searchEntra = async (query: string) => {
		setEntraSearch(query)
		if (query.length < 2) {
			setEntraResults([])
			return
		}
		try {
			const response = await apiClient.get<EntraSearchResult[]>(
				`/entra-directory/search?q=${encodeURIComponent(query)}`,
			)
			setEntraResults(response.data || [])
		} catch {
			setEntraResults([])
		}
	}

	const selectEntraEntity = (entity: EntraSearchResult) => {
		setFormData((prev) => ({
			...prev,
			entraDirectoryId: entity.entraDirectoryId,
			displayName: entity.displayName,
			firstName: entity.firstName,
			lastName: entity.lastName,
		}))
		setEntraSearch(entity.displayName + (entity.email ? ` (${entity.email})` : ''))
		setEntraResults([])
	}

	const openCreateModal = () => {
		resetForm()
		setShowCreateModal(true)
	}

	const openEditModal = (employee: IEmployee) => {
		setEditingEmployee(employee)
		setFormData({
			displayName: employee.displayName,
			firstName: employee.firstName,
			lastName: employee.lastName,
			contractTypeId: employee.contractTypeId,
			geoLocationId: employee.geoLocationId,
			departmentId: employee.departmentId,
			leadershipTypeId: employee.leadershipTypeId,
			personTypeId: employee.personTypeId,
			contractorId: employee.contractorId,
			managerId: employee.managerId,
			isActive: employee.isActive,
		})
	}

	const applyFilters = () => {
		setQueryParams((prev) => ({ ...prev, page: 1, filters: { ...tempFilters } }))
	}

	const clearFilters = () => {
		setTempFilters({})
		setQueryParams((prev) => ({ ...prev, page: 1, filters: {} }))
	}

	const handleSort = (field: string) => {
		setQueryParams((prev) => ({
			...prev,
			sort: field,
			order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc',
		}))
	}

	const getMasterLabel = (id: string | null | undefined, items: { label: string }[] | undefined, idField: string) => {
		if (!id || !items) return '-'
		const item = items.find((i) => (i as Record<string, unknown>)[idField] === id)
		return item?.label || '-'
	}

	const getManagerLabel = (id: string | null | undefined, items: IEmployee[] | undefined) => {
		if (!id || !items) return '-'
		return items.find((e) => e.employeeId === id)?.displayName || '-'
	}

	// Render modal form fields (shared between create and edit)
	const renderFormFields = () => (
		<div className="space-y-4">
			{/* Entra link (on create, or on edit if not already linked) */}
			{(showCreateModal || (editingEmployee && !editingEmployee.entraDirectoryId)) && (
				<div className="relative">
					<label className="block text-sm font-medium mb-1">Link from Entra Directory (optional)</label>
					<Input
						placeholder="Search by name or email..."
						value={entraSearch}
						onChange={(e) => void searchEntra(e.target.value)}
					/>
					{entraResults.length > 0 && (
						<div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
							{entraResults.map((r) => (
								<button
									key={r.entraDirectoryId}
									type="button"
									className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
									onClick={() => selectEntraEntity(r)}
								>
									{r.displayName} {r.email && <span className="text-gray-400">({r.email})</span>}
									<Badge className="ml-2 text-xs">{r.entityType}</Badge>
								</button>
							))}
						</div>
					)}
				</div>
			)}
			<div>
				<label className="block text-sm font-medium mb-1">Display Name *</label>
				<Input
					value={formData.displayName}
					onChange={(e) => setFormData((prev) => ({ ...prev, displayName: e.target.value }))}
				/>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<div>
					<label className="block text-sm font-medium mb-1">First Name</label>
					<Input
						value={formData.firstName || ''}
						onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value || undefined }))}
					/>
				</div>
				<div>
					<label className="block text-sm font-medium mb-1">Last Name</label>
					<Input
						value={formData.lastName || ''}
						onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value || undefined }))}
					/>
				</div>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<MasterComboRow
					label="Contract Type"
					options={contractTypeOptions}
					value={formData.contractTypeId}
					onChange={(next) => setFormData((prev) => ({ ...prev, contractTypeId: next ?? null }))}
				/>
				<MasterComboRow
					label="Geo Location"
					options={geoLocationOptions}
					value={formData.geoLocationId}
					onChange={(next) => setFormData((prev) => ({ ...prev, geoLocationId: next ?? null }))}
				/>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<MasterComboRow
					label="Department"
					options={departmentOptions}
					value={formData.departmentId}
					onChange={(next) => setFormData((prev) => ({ ...prev, departmentId: next ?? null }))}
				/>
				<MasterComboRow
					label="Leadership Type"
					options={leadershipTypeOptions}
					value={formData.leadershipTypeId}
					onChange={(next) => setFormData((prev) => ({ ...prev, leadershipTypeId: next ?? null }))}
				/>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<MasterComboRow
					label="Person Type"
					options={personTypeOptions}
					value={formData.personTypeId}
					onChange={(next) => setFormData((prev) => ({ ...prev, personTypeId: next ?? null }))}
				/>
				<MasterComboRow
					label="Contractor"
					options={contractorOptions}
					value={formData.contractorId}
					onChange={(next) => setFormData((prev) => ({ ...prev, contractorId: next ?? null }))}
				/>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<MasterComboRow
					label="Manager"
					options={managerPickerOptions}
					value={formData.managerId}
					onChange={(next) => setFormData((prev) => ({ ...prev, managerId: next ?? null }))}
				/>
				<div />
			</div>
			{/* isActive toggle (only on edit) */}
			{editingEmployee && (
				<div className="flex items-center gap-2">
					<input
						type="checkbox"
						id="isActive"
						checked={formData.isActive ?? true}
						onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
					/>
					<label htmlFor="isActive" className="text-sm font-medium">
						Active
					</label>
				</div>
			)}
			{/* Comment (optional) */}
			<div>
				<label className="block text-sm font-medium mb-1">Comment (optional)</label>
				<textarea
					className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]"
					placeholder="Reason for this change..."
					value={formData.comment || ''}
					onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value || undefined }))}
				/>
			</div>
		</div>
	)

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h1 className="text-2xl font-bold text-gray-900">Employees</h1>
				<Button onClick={openCreateModal}>Create Employee</Button>
			</div>

			{/* Filters */}
			<div
				className="bg-white rounded-lg shadow p-4 space-y-3"
				onKeyDown={(e) => {
					if (e.key === 'Enter') applyFilters()
				}}
			>
				<div className="grid grid-cols-4 gap-3">
					<Input
						placeholder="Display Name"
						value={tempFilters.displayName || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, displayName: e.target.value }))}
					/>
					<Input
						placeholder="First Name"
						value={tempFilters.firstName || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, firstName: e.target.value }))}
					/>
					<Input
						placeholder="Last Name"
						value={tempFilters.lastName || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, lastName: e.target.value }))}
					/>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.isActive || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, isActive: e.target.value }))}
					>
						<option value="">All Status</option>
						<option value="true">Active</option>
						<option value="false">Inactive</option>
					</select>
				</div>
				<div className="grid grid-cols-7 gap-3">
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.contractTypeId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, contractTypeId: e.target.value }))}
					>
						<option value="">All Contract Types</option>
						{contractTypes?.map((ct) => (
							<option key={ct.contractTypeId} value={ct.contractTypeId}>
								{ct.label}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.geoLocationId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, geoLocationId: e.target.value }))}
					>
						<option value="">All Locations</option>
						{geoLocations?.map((gl) => (
							<option key={gl.geoLocationId} value={gl.geoLocationId}>
								{gl.label}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.departmentId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, departmentId: e.target.value }))}
					>
						<option value="">All Departments</option>
						{departmentOptions.map((d) => (
							<option key={d.value} value={d.value}>
								{d.label}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.leadershipTypeId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, leadershipTypeId: e.target.value }))}
					>
						<option value="">All Leadership</option>
						{leadershipTypes?.map((lt) => (
							<option key={lt.leadershipTypeId} value={lt.leadershipTypeId}>
								{lt.label}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.personTypeId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, personTypeId: e.target.value }))}
					>
						<option value="">All Person Types</option>
						{personTypes?.map((pt) => (
							<option key={pt.personTypeId} value={pt.personTypeId}>
								{pt.label}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.contractorId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, contractorId: e.target.value }))}
					>
						<option value="">All Contractors</option>
						{contractors?.map((c) => (
							<option key={c.contractorId} value={c.contractorId}>
								{c.name}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.managerId || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, managerId: e.target.value }))}
					>
						<option value="">All Managers</option>
						{managerFilterOptions.map((m) => (
							<option key={m.value} value={m.value}>
								{m.label}
							</option>
						))}
					</select>
					<div className="flex gap-2">
						<Button onClick={applyFilters} size="sm">
							Filter
						</Button>
						<Button onClick={clearFilters} variant="outline" size="sm">
							Clear
						</Button>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="bg-white rounded-lg shadow overflow-hidden">
				{isLoading ? (
					<div className="p-8 text-center text-gray-500">Loading...</div>
				) : !employees || employees.length === 0 ? (
					<div className="p-8 text-center text-gray-500">No employees found</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="cursor-pointer" onClick={() => handleSort('displayName')}>
									Display Name {queryParams.sort === 'displayName' && (queryParams.order === 'asc' ? '↑' : '↓')}
								</TableHead>
								<TableHead>First Name</TableHead>
								<TableHead>Last Name</TableHead>
								<TableHead>Contract Type</TableHead>
								<TableHead>Geo Location</TableHead>
								<TableHead>Department</TableHead>
								<TableHead>Manager</TableHead>
								<TableHead>Active</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{employees.map((employee) => (
								<TableRow key={employee.employeeId}>
									<TableCell className="font-medium">{employee.displayName}</TableCell>
									<TableCell>{employee.firstName || '-'}</TableCell>
									<TableCell>{employee.lastName || '-'}</TableCell>
									<TableCell className="text-sm">
										{getMasterLabel(employee.contractTypeId, contractTypes, 'contractTypeId')}
									</TableCell>
									<TableCell className="text-sm">
										{getMasterLabel(employee.geoLocationId, geoLocations, 'geoLocationId')}
									</TableCell>
									<TableCell className="text-sm">
										{getMasterLabel(employee.departmentId, flatDepartments, 'departmentId')}
									</TableCell>
									<TableCell className="text-sm">{getManagerLabel(employee.managerId, allEmployees)}</TableCell>
									<TableCell>
										<Badge className={employee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
											{employee.isActive ? 'Active' : 'Inactive'}
										</Badge>
									</TableCell>
									<TableCell>
										<div className="flex gap-1">
											<Button variant="ghost" size="sm" onClick={() => openEditModal(employee)}>
												Edit
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-red-600"
												onClick={() => setDeleteConfirm(employee.employeeId)}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Pagination */}
			<div className="flex justify-between items-center">
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-500">Page size:</span>
					{[20, 50, 100, 200, 500].map((size) => (
						<Button
							key={size}
							variant={queryParams.limit === size ? 'default' : 'outline'}
							size="sm"
							onClick={() => setQueryParams((prev) => ({ ...prev, limit: size, page: 1 }))}
						>
							{size}
						</Button>
					))}
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={queryParams.page <= 1}
						onClick={() => setQueryParams((prev) => ({ ...prev, page: prev.page - 1 }))}
					>
						Previous
					</Button>
					<span className="px-3 py-1 text-sm">Page {queryParams.page}</span>
					<Button
						variant="outline"
						size="sm"
						disabled={!employees || employees.length < queryParams.limit}
						onClick={() => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }))}
					>
						Next
					</Button>
				</div>
			</div>

			{/* Create Modal */}
			{showCreateModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
						<h2 className="text-lg font-bold mb-4">Create Employee</h2>
						{renderFormFields()}
						<div className="flex justify-end gap-2 mt-6">
							<Button
								variant="outline"
								onClick={() => {
									setShowCreateModal(false)
									resetForm()
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={() => createMutation.mutate(formData)}
								disabled={!formData.displayName || createMutation.isPending}
							>
								{createMutation.isPending ? 'Creating...' : 'Create'}
							</Button>
						</div>
						{createMutation.isError && <p className="text-sm text-red-600 mt-2">Failed to create employee</p>}
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{editingEmployee && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
						<h2 className="text-lg font-bold mb-4">Edit Employee</h2>
						{editingEmployee.entraDirectoryId && (
							<p className="text-sm text-gray-500 mb-4">
								Linked to Entra Directory: {editingEmployee.entraDirectoryId}
							</p>
						)}
						{renderFormFields()}
						<div className="flex justify-end gap-2 mt-6">
							<Button
								variant="outline"
								onClick={() => {
									setEditingEmployee(null)
									resetForm()
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={() => updateMutation.mutate({ id: editingEmployee.employeeId, data: formData })}
								disabled={updateMutation.isPending}
							>
								{updateMutation.isPending ? 'Saving...' : 'Save'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Confirmation */}
			{deleteConfirm && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
						<h2 className="text-lg font-bold mb-2">Delete Employee?</h2>
						<p className="text-sm text-gray-500 mb-2">This action cannot be undone.</p>
						<textarea
							className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] mb-4"
							placeholder="Reason for deletion (optional)..."
							value={deleteComment}
							onChange={(e) => setDeleteComment(e.target.value)}
						/>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setDeleteConfirm(null)
									setDeleteComment('')
								}}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={() => {
									deleteMutation.mutate(deleteConfirm)
									setDeleteComment('')
								}}
								disabled={deleteMutation.isPending}
							>
								{deleteMutation.isPending ? 'Deleting...' : 'Delete'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

interface MasterDepartmentOption {
	departmentId: string
	code: string
	label: string
	parentId?: string | null
}

interface MasterDepartmentTreeNode {
	departmentId: string
	code: string
	label: string
	parentId: string | null
	children?: MasterDepartmentTreeNode[]
}

/**
 * Flatten the nested tree returned by `/employees/master/departments` so the
 * filter dropdown, table column, and picker can all consume a flat list.
 * `parentId` is preserved from the wire (the API includes it on every node),
 * which is what `buildDepartmentOptions` walks to build "Keenfolks › Tech › …"
 * breadcrumb labels.
 */
function flattenDepartmentTree(tree: MasterDepartmentTreeNode[]): MasterDepartmentOption[] {
	const out: MasterDepartmentOption[] = []
	const walk = (nodes: MasterDepartmentTreeNode[]): void => {
		for (const node of nodes) {
			out.push({ departmentId: node.departmentId, code: node.code, label: node.label, parentId: node.parentId })
			if (node.children && node.children.length > 0) walk(node.children)
		}
	}
	walk(tree)
	return out
}

/**
 * Project flat master_departments into `Keenfolks › Tech › Tech Frontend` style
 * options sorted so children immediately follow their parent. Same breadcrumb
 * convention used by the request-center departments data source.
 */
function buildDepartmentOptions(all: MasterDepartmentOption[]): Array<{ label: string; value: string }> {
	const byId = new Map(all.map((d) => [d.departmentId, d]))
	const pathOf = (d: MasterDepartmentOption): string => {
		const chain: MasterDepartmentOption[] = []
		let cursor: MasterDepartmentOption | undefined = d.parentId ? byId.get(d.parentId) : undefined
		while (cursor) {
			chain.unshift(cursor)
			cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined
		}
		return [...chain.map((a) => a.label), d.label].join(' \u203a ')
	}
	return all.map((d) => ({ value: d.departmentId, label: pathOf(d) })).sort((a, b) => a.label.localeCompare(b.label))
}

function MasterComboRow({
	label,
	options,
	value,
	onChange,
}: {
	label: string
	options: Array<{ label: string; value: string }>
	value?: string | null
	onChange: (next: string | undefined) => void
}) {
	return (
		<div className="space-y-1">
			<label className="block text-sm font-medium mb-1">{label}</label>
			<StaticComboBoxField
				ariaLabel={label}
				placeholder="None"
				options={options}
				value={value ?? undefined}
				onChange={onChange}
			/>
		</div>
	)
}
